import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Search, Trash2, Edit3, ChevronLeft, ChevronRight, Users2, ShieldCheck, Shield, Crown } from 'lucide-react';
import { PAPER_BASE } from '../config';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;

export default function PaperAdminMemberPage() {
  const [members, setMembers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(true);

  const [editMember, setEditMember] = useState(null);
  const [editForm, setEditForm] = useState({});

  const token = localStorage.getItem('token');

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/member/admin/list`, {
        params: { page, limit: 20, search, site: 'paper' },
        headers: { Authorization: `Bearer ${token}` },
      });
      setMembers(res.data.members);
      setTotal(res.data.total);
      setTotalPages(res.data.totalPages);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, search, token]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`"${name}" 회원을 삭제하시겠습니까?`)) return;
    try {
      await axios.delete(`${API}/member/admin/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      alert('삭제되었습니다.');
      fetchMembers();
    } catch (err) {
      alert(err.response?.data?.errorMessage || '삭제에 실패했습니다.');
    }
  };

  const handleToggleAdmin = async (member) => {
    const action = member.adminYn === 'Y' ? '관리자 해제' : '관리자 지정';
    if (!window.confirm(`"${member.name || member.loginId}"을(를) ${action}하시겠습니까?`)) return;
    try {
      await axios.put(`${API}/member/admin/${member.id}/toggle-admin`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchMembers();
    } catch (err) {
      alert(err.response?.data?.errorMessage || '변경에 실패했습니다.');
    }
  };

  const openEdit = (member) => {
    setEditMember(member);
    setEditForm({
      name: member.name || '',
      nickName: member.nickName || '',
      emailId: member.emailId || '',
      emailDomain: member.emailDomain || '',
      phone1: member.phone1 || '',
      phone2: member.phone2 || '',
      phone3: member.phone3 || '',
      password: '',
    });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      Object.entries(editForm).forEach(([key, val]) => {
        if (key === 'password' && !val) return;
        formData.append(key, val);
      });
      await axios.put(`${API}/member/admin/${editMember.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      alert('수정되었습니다.');
      setEditMember(null);
      fetchMembers();
    } catch (err) {
      alert(err.response?.data?.errorMessage || '수정에 실패했습니다.');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
  };

  const inputClass =
    'w-full px-3 py-2 rounded-lg border border-white/10 bg-[#1a1428] text-sm text-slate-200 placeholder-slate-500 outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-transparent transition';

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      {/* Admin Nav */}
      <div className="flex items-center gap-4 mb-6 border-b border-white/10 pb-3 overflow-x-auto">
        <Link to={`${PAPER_BASE}/admin/members`} className="text-sm font-medium text-violet-400 border-b-2 border-violet-400 pb-1 whitespace-nowrap">회원 관리</Link>
        <Link to={`${PAPER_BASE}/admin/works`} className="text-sm font-medium text-white/40 hover:text-white/60 transition-colors pb-1 whitespace-nowrap">행사 관리</Link>
        <Link to={`${PAPER_BASE}/admin/services`} className="text-sm font-medium text-white/40 hover:text-white/60 transition-colors pb-1 whitespace-nowrap">서비스 관리</Link>
        <Link to={`${PAPER_BASE}/admin/partners`} className="text-sm font-medium text-white/40 hover:text-white/60 transition-colors pb-1 whitespace-nowrap">파트너 관리</Link>
        <Link to={`${PAPER_BASE}/admin/blogs`} className="text-sm font-medium text-white/40 hover:text-white/60 transition-colors pb-1 whitespace-nowrap">스토리 관리</Link>
      </div>

      {/* 페이지 헤더 */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-violet-500/15 flex items-center justify-center">
            <Users2 size={20} className="text-violet-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">일반회원관리</h1>
            <p className="text-sm text-slate-400">전체 {total}명</p>
          </div>
        </div>

        <form onSubmit={handleSearch} className="flex items-center gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-64">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="아이디, 이름, 닉네임 검색"
              className="w-full pl-9 pr-4 py-2 bg-white/[0.05] border border-white/10 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-violet-400/50 transition"
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-violet-500 hover:bg-violet-600 text-white text-sm font-medium rounded-lg transition-colors">
            검색
          </button>
        </form>
      </div>

      {/* 테이블 */}
      <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/[0.06]">
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wide">아이디</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wide">이름</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wide hidden md:table-cell">닉네임</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wide hidden lg:table-cell">이메일</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wide hidden lg:table-cell">전화번호</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-400 uppercase tracking-wide hidden md:table-cell">관리자</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wide hidden sm:table-cell">가입일</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-400 uppercase tracking-wide w-28">관리</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-4 py-12 text-center text-slate-500">
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-3 h-3 rounded-full border-2 border-violet-400 border-t-transparent animate-spin" />
                      로딩 중...
                    </div>
                  </td>
                </tr>
              ) : members.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-12 text-center text-slate-500">
                    {search ? '검색 결과가 없습니다.' : '등록된 회원이 없습니다.'}
                  </td>
                </tr>
              ) : (
                members.map((m) => (
                  <tr key={m.id} className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors">
                    <td className="px-4 py-3 text-slate-200 font-medium">{m.loginId}</td>
                    <td className="px-4 py-3 text-slate-300">{m.name}</td>
                    <td className="px-4 py-3 text-slate-400 hidden md:table-cell">{m.nickName || '-'}</td>
                    <td className="px-4 py-3 text-slate-400 hidden lg:table-cell">
                      {m.emailId && m.emailDomain ? `${m.emailId}@${m.emailDomain}` : '-'}
                    </td>
                    <td className="px-4 py-3 text-slate-400 hidden lg:table-cell">
                      {m.phone1 && m.phone2 && m.phone3 ? `${m.phone1}-${m.phone2}-${m.phone3}` : '-'}
                    </td>
                    <td className="px-4 py-3 text-center hidden md:table-cell">
                      {m.superAdminYn === 'Y' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-500/20 text-amber-400">
                          <Crown size={11} /> 슈퍼관리자
                        </span>
                      ) : m.adminYn === 'Y' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-violet-500/20 text-violet-400">
                          <ShieldCheck size={11} /> 관리자
                        </span>
                      ) : (
                        <span className="text-slate-600 text-xs">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-500 hidden sm:table-cell">{formatDate(m.createdAt)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          title={m.adminYn === 'Y' ? '관리자 해제' : '관리자 지정'}
                          className={`p-1.5 rounded-md transition-colors ${m.adminYn === 'Y' ? 'text-violet-400 hover:text-violet-300 hover:bg-violet-400/10' : 'text-slate-400 hover:text-violet-400 hover:bg-violet-400/10'}`}
                          onClick={() => handleToggleAdmin(m)}>
                          {m.adminYn === 'Y' ? <ShieldCheck size={14} /> : <Shield size={14} />}
                        </button>
                        <button
                          title="수정"
                          className="p-1.5 rounded-md text-slate-400 hover:text-violet-400 hover:bg-violet-400/10 transition-colors"
                          onClick={() => openEdit(m)}>
                          <Edit3 size={14} />
                        </button>
                        <button
                          title="삭제"
                          className="p-1.5 rounded-md text-slate-400 hover:text-rose-400 hover:bg-rose-400/10 transition-colors"
                          onClick={() => handleDelete(m.id, m.name)}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 px-4 py-4 border-t border-white/[0.06]">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-30 disabled:pointer-events-none">
              <ChevronLeft size={16} />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
              .reduce((acc, p, idx, arr) => {
                if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                acc.push(p);
                return acc;
              }, [])
              .map((p, idx) =>
                p === '...' ? (
                  <span key={`dot-${idx}`} className="text-slate-600 px-1">...</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`w-8 h-8 rounded-md text-sm font-medium transition-colors ${
                      p === page
                        ? 'bg-violet-500 text-white'
                        : 'text-slate-400 hover:text-white hover:bg-white/10'
                    }`}>
                    {p}
                  </button>
                )
              )}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-30 disabled:pointer-events-none">
              <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>

      {/* 수정 모달 */}
      {editMember && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-md bg-[#1a1428] rounded-2xl border border-white/10 shadow-2xl">
            <div className="px-6 pt-6 pb-4 border-b border-white/[0.06]">
              <h2 className="text-lg font-bold text-white">회원 정보 수정</h2>
              <p className="text-sm text-slate-400 mt-0.5">{editMember.loginId}</p>
            </div>

            <form onSubmit={handleEditSubmit} className="px-6 py-5 space-y-4">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-400">이름</label>
                <input
                  type="text"
                  className={inputClass}
                  value={editForm.name}
                  onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-400">닉네임</label>
                <input
                  type="text"
                  className={inputClass}
                  value={editForm.nickName}
                  onChange={(e) => setEditForm((p) => ({ ...p, nickName: e.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-400">이메일</label>
                <div className="flex items-center gap-1.5">
                  <input
                    type="text"
                    className={`${inputClass} flex-1`}
                    value={editForm.emailId}
                    onChange={(e) => setEditForm((p) => ({ ...p, emailId: e.target.value }))}
                  />
                  <span className="text-slate-500 text-sm">@</span>
                  <input
                    type="text"
                    className={`${inputClass} flex-1`}
                    value={editForm.emailDomain}
                    onChange={(e) => setEditForm((p) => ({ ...p, emailDomain: e.target.value }))}
                  />
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-400">전화번호</label>
                <div className="flex items-center gap-1.5">
                  <input
                    type="text"
                    className={inputClass}
                    value={editForm.phone1}
                    onChange={(e) => setEditForm((p) => ({ ...p, phone1: e.target.value }))}
                    maxLength={3}
                  />
                  <span className="text-slate-500">-</span>
                  <input
                    type="text"
                    className={inputClass}
                    value={editForm.phone2}
                    onChange={(e) => setEditForm((p) => ({ ...p, phone2: e.target.value }))}
                    maxLength={4}
                  />
                  <span className="text-slate-500">-</span>
                  <input
                    type="text"
                    className={inputClass}
                    value={editForm.phone3}
                    onChange={(e) => setEditForm((p) => ({ ...p, phone3: e.target.value }))}
                    maxLength={4}
                  />
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-400">
                  비밀번호 변경 <span className="text-slate-500 normal-case font-normal">(입력 시에만 변경)</span>
                </label>
                <input
                  type="password"
                  className={inputClass}
                  placeholder="새 비밀번호"
                  value={editForm.password}
                  onChange={(e) => setEditForm((p) => ({ ...p, password: e.target.value }))}
                  autoComplete="new-password"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setEditMember(null)}
                  className="px-4 py-2 text-sm font-medium text-slate-400 hover:text-white rounded-lg hover:bg-white/5 transition-colors">
                  취소
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-violet-500 hover:bg-violet-600 text-white text-sm font-medium rounded-lg transition-colors">
                  저장
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
